import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CustomFieldService, ToastService } from '@app/core';
import { CustomField, CustomFieldChoice } from '@app/shared';


@Component({
    selector: 'app-custom-field',
    templateUrl: './custom-field.component.html',
    styleUrls: ['./custom-field.component.css']
})
export class CustomFieldComponent implements OnInit {

    field: CustomField;
    private _mode: 'create' | 'update';

    // Widget options for the field_type select. Labels are Danish to match the
    // rest of the admin UI; radio = pick one, checkbox = pick several.
    readonly fieldTypes = [
        { value: 'text', label: 'Tekst' },
        { value: 'textarea', label: 'Langt tekstfelt' },
        { value: 'radio', label: 'Valg – én mulighed' },
        { value: 'checkbox', label: 'Valg – flere muligheder' }
    ];

    constructor(
        private customFieldService: CustomFieldService,
        private route: ActivatedRoute,
        private router: Router,
        private toast: ToastService
    ) { }

    ngOnInit() {
        this.route.data.subscribe(
            (data: { field: CustomField }) => {
                this._mode = !!data.field.id ? 'update' : 'create';
                this.field = data.field;
                this.sortChoices();
            }
        );
    }

    get mode() {
        return this._mode;
    }

    get isCreateMode() {
        return this._mode === 'create';
    }

    get isEditMode() {
        return this._mode === 'update';
    }

    get hasChoices() {
        return this.field.field_type === 'radio'
            || this.field.field_type === 'checkbox';
    }

    addChoice() {
        const choice = new CustomFieldChoice();
        choice.order = this.field.choices.length;
        this.field.choices.push(choice);
        this.sortChoices();
    }

    removeChoice(choice: CustomFieldChoice) {
        // New (unsaved) choices are dropped outright; existing ones are only
        // retired (is_active=false) so submitted answers referencing them
        // survive. Mirrors the backend "never delete" rule.
        if (choice.id) {
            choice.is_active = false;
        } else {
            const index = this.field.choices.indexOf(choice);
            if (index > -1) {
                this.field.choices.splice(index, 1);
            }
        }
        this.sortChoices();
    }

    restoreChoice(choice: CustomFieldChoice) {
        choice.is_active = true;
        this.sortChoices();
    }

    private sortChoices() {
        // Editor-only presentation: active choices on top, then by order.
        // The doctor form only sees active choices, so this grouping is
        // meaningful only here in the admin editor.
        this.field.choices.sort((a, b) =>
            (Number(b.is_active) - Number(a.is_active)) || (a.order - b.order));
    }

    save() {
        if (this._mode === 'create') {
            this.create();
        } else if (this._mode === 'update') {
            this.update();
        }
    }

    activate() {
        this.customFieldService.activate(this.field).subscribe(
            field => this.success(field, 'Felt aktiveret'),
            error => {
                console.error('Error during custom field activation', error);
                this.toast.error('Fejl: Felt ikke aktiveret');
            }
        );
    }

    deactivate() {
        this.customFieldService.deactivate(this.field).subscribe(
            field => this.success(field, 'Felt deaktiveret'),
            error => {
                console.error('Error during custom field deactivation', error);
                this.toast.error('Fejl: Felt ikke deaktiveret');
            }
        );
    }

    private create() {
        this.customFieldService.create(this.field).subscribe(
            field => this.success(field),
            error => this.fail(error),
            () => this.router.navigate(['/admin/custom-field-list'])
        );
    }

    private update() {
        this.customFieldService.update(this.field).subscribe(
            field => this.success(field),
            error => this.fail(error)
        );
    }

    private success(field: CustomField, toastMessage = 'Felt gemt') {
        this.field = field;
        this.toast.success(toastMessage);
    }

    private fail(error: any) {
        console.error('Error during custom field save', error);
        this.toast.error('Fejl: Opdatering ikke gemt');
    }

}
